/**
 * The bet log.
 *
 * Its OWN query, and that is the point: the panels above are the exposure
 * PROJECTION over open matched positions, while this is the LOG, and a log can
 * show settled, void and cancelled bets that a projection by definition cannot.
 * Their totals are not supposed to agree, and the card's subtitle says so
 * outright rather than leaving an operator to discover it by subtraction.
 *
 * The console's global date range is deliberately NOT applied here. A market is
 * not a reporting period: silently imposing a 30-day window would hide bets on
 * a three-month outright and the operator would have no way to know. The
 * global-range principle is real, and this is a considered exception to it.
 */

import { useDeferredValue, useMemo } from 'react'
import {
  Button,
  Card,
  Chip,
  DataTable,
  Field,
  Input,
  Pager,
  SearchInput,
  Select,
  StateBlock,
  type Column,
  type Tone,
} from '@w7/ui'
import { AccountLink, Amount, exportCsv } from '@/app/bits'
import { useMarketBets } from '@/app/queries'
import { EMPTY, count, isoDay, lll, odds } from '@/lib/format'
import type { Bet, BetStatus, MarketExposure } from '@/data/types'
import {
  BET_ROW_CAPS,
  BET_SIDE_FILTERS,
  BET_STATUS_FILTERS,
  CLEARED_BET_FILTERS,
  admitsSettled,
  betFiltersActive,
  betQueryOf,
  type BetFilters,
} from './betFilters'

const STATUS: Record<BetStatus, { label: string; tone: Tone }> = {
  unmatched: { label: 'Unmatched', tone: 'warn' },
  matched: { label: 'Matched', tone: 'info' },
  settled: { label: 'Settled', tone: 'muted' },
  void: { label: 'Void', tone: 'muted' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
}

export function MarketBets({
  marketId,
  market,
  eventName,
  filters,
  onPatch,
}: {
  marketId: string
  /** Null while the book is loading or unavailable — the log still works. */
  market: MarketExposure | null
  eventName: string
  filters: BetFilters
  onPatch: (next: Record<string, string | null>) => void
}) {
  // Each keystroke would otherwise be its own query. Deferring keeps the last
  // good page on screen while the next one lands, as Exposure already does.
  const deferredSearch = useDeferredValue(filters.q)
  const query = useMemo(
    () => betQueryOf(marketId, { ...filters, q: deferredSearch }),
    [marketId, filters, deferredSearch],
  )
  const { data, isLoading, isError, error, refetch } = useMarketBets(query)

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / filters.rows))
  const filtered = betFiltersActive(filters)

  const members = useMemo(() => {
    const map = new Map<string, { username: string }>()
    for (const member of data?.members ?? []) map.set(member.id, { username: member.username })
    return map
  }, [data])

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, bet) => ({
          stake: acc.stake + bet.stake,
          matched: acc.matched + bet.matched,
          unmatched: acc.unmatched + bet.unmatched,
        }),
        { stake: 0, matched: 0, unmatched: 0 },
      ),
    [rows],
  )

  /** Any filter change resets the page — page 7 of a different result set is a lie. */
  const patchFilter = (next: Record<string, string | null>) => onPatch({ ...next, page: null })

  const columns = useMemo<Column<Bet>[]>(
    () => [
      {
        id: 'member',
        header: 'Member',
        cell: (bet) => {
          const member = members.get(bet.memberId)
          return member ? <AccountLink id={bet.memberId} username={member.username} /> : EMPTY
        },
        sortValue: (bet) => members.get(bet.memberId)?.username ?? null,
      },
      {
        id: 'placed',
        header: 'Placed',
        hideBelow: 720,
        cell: (bet) => lll(bet.placedAt),
        sortValue: (bet) => bet.placedAt,
      },
      {
        id: 'matched-at',
        header: 'Matched at',
        hideBelow: 1500,
        help: 'Blank while nothing has matched. For a part-matched bet this is when the first of it matched.',
        cell: (bet) => (bet.matchedAt ? lll(bet.matchedAt) : EMPTY),
        sortValue: (bet) => bet.matchedAt,
      },
      {
        id: 'selection',
        header: 'Selection',
        cell: (bet) => (
          <span className="w7-truncate" title={bet.selection}>
            {bet.selection}
          </span>
        ),
        sortValue: (bet) => bet.selection,
      },
      {
        id: 'side',
        header: 'Side',
        cell: (bet) => (
          <Chip size="sm" tone={bet.side === 'back' ? 'back' : 'lay'}>
            {bet.side === 'back' ? 'Back' : 'Lay'}
          </Chip>
        ),
        sortValue: (bet) => bet.side,
      },
      {
        id: 'odds-placed',
        header: 'Odds placed',
        numeric: true,
        hideBelow: 1280,
        cell: (bet) => odds(bet.oddsPlaced),
        sortValue: (bet) => bet.oddsPlaced,
      },
      {
        id: 'odds-matched',
        header: 'Odds matched',
        numeric: true,
        hideBelow: 1080,
        help: 'Blank until the bet is matched — an unmatched bet has no matched price yet.',
        cell: (bet) => odds(bet.oddsMatched),
        sortValue: (bet) => bet.oddsMatched,
      },
      {
        id: 'stake',
        header: 'Stake',
        numeric: true,
        cell: (bet) => <Amount value={bet.stake} />,
        sortValue: (bet) => bet.stake,
        total: <Amount value={totals.stake} />,
      },
      {
        id: 'matched',
        header: 'Matched',
        numeric: true,
        hideBelow: 900,
        // Exactly 0 renders as ₹0.00 at dimmer emphasis, never as the em dash:
        // `money()` refuses to make a missing number look like a zero, and the
        // reverse has to hold too.
        cell: (bet) => (
          <span className={bet.matched === 0 ? 'w7-dimmer' : undefined}>
            <Amount value={bet.matched} />
          </span>
        ),
        sortValue: (bet) => bet.matched,
        total: <Amount value={totals.matched} />,
      },
      {
        id: 'unmatched',
        header: 'Unmatched',
        numeric: true,
        hideBelow: 900,
        help: 'Offered but not yet taken. It carries no liability until it matches.',
        cell: (bet) => (
          <span className={bet.unmatched === 0 ? 'w7-dimmer' : undefined}>
            <Amount value={bet.unmatched} />
          </span>
        ),
        sortValue: (bet) => bet.unmatched,
        total: <Amount value={totals.unmatched} />,
      },
      {
        id: 'liability',
        header: 'Liability',
        numeric: true,
        hideBelow: 1080,
        help: "What the member loses if this bet loses. Deliberately not totalled — see the column's total cell.",
        cell: (bet) => <Amount value={bet.liability} />,
        sortValue: (bet) => bet.liability,
        // Liabilities on different selections are mutually exclusive, so a sum
        // would invent a loss that cannot happen. The book above is the total.
        total: (
          <span
            className="w7-dim"
            title="Not totalled: these liabilities sit on outcomes that cannot all happen, so adding them would overstate the risk. The book table above is the true total."
          >
            {EMPTY}
          </span>
        ),
      },
      {
        id: 'potential',
        header: 'Potential profit',
        numeric: true,
        hideBelow: 1080,
        help: 'What the member wins if this bet wins, net of stake. That win is the book paying out.',
        cell: (bet) => <Amount value={bet.potentialProfit} />,
        sortValue: (bet) => bet.potentialProfit,
        total: (
          <span
            className="w7-dim"
            title="Not totalled for the same reason as liability: only one selection can win."
          >
            {EMPTY}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (bet) => {
          const status = STATUS[bet.status]
          return (
            <Chip size="sm" tone={status.tone}>
              {status.label}
            </Chip>
          )
        },
        sortValue: (bet) => bet.status,
      },
    ],
    [members, totals],
  )

  const exportRows = () =>
    exportCsv(
      `market-bets-${marketId}-${isoDay(Date.now())}.csv`,
      rows.map((bet) => ({
        // Event and market are constant on this page and were dropped from the
        // table as noise down 500 rows — but a FILE has to identify itself.
        bet_id: bet.id,
        member_username: members.get(bet.memberId)?.username ?? '',
        member_id: bet.memberId,
        event: eventName,
        market: market?.marketName ?? bet.marketName,
        selection: bet.selection,
        side: bet.side,
        placed_at: bet.placedAt,
        matched_at: bet.matchedAt ?? '',
        odds_placed: bet.oddsPlaced,
        odds_matched: bet.oddsMatched ?? '',
        stake: bet.stake,
        matched: bet.matched,
        unmatched: bet.unmatched,
        member_liability: bet.liability,
        member_potential_profit: bet.potentialProfit,
        status: bet.status,
        result: bet.result ?? '',
        member_pnl: bet.pnl ?? '',
      })),
    )

  const selectionOptions = [
    { value: '', label: 'All selections' },
    ...(market?.selections ?? []).map((selection) => ({
      value: selection.name,
      label: selection.name,
    })),
  ]

  return (
    <Card
      title="Bets on this market"
      subtitle="The book above counts open matched positions. This list is every bet, including settled ones — the two are not the same number. Liability and potential profit are the member's figures: a member win is a payout from your book, and per-selection liabilities are never added because they sit on outcomes that cannot all happen."
      actions={
        <span className="w7-row" style={{ gap: 'var(--w7-s2)' }}>
          <Chip size="sm">
            {filtered
              ? `${count(total)} ${total === 1 ? 'bet matches' : 'bets match'}`
              : `${count(total)} ${total === 1 ? 'bet' : 'bets'} on this market`}
          </Chip>
          <Button
            variant="ghost"
            size="sm"
            icon="download"
            onClick={exportRows}
            disabled={rows.length === 0}
            title={
              rows.length === 0
                ? 'Nothing to export under these filters'
                : `Export the ${count(rows.length)} bets listed on this page`
            }
          >
            Export CSV
          </Button>
        </span>
      }
      flush
    >
      <div style={{ padding: 'var(--w7-s4)' }}>
        <div className="w7-betfilters">
          {/* Not a `Field`: SearchInput owns its own accessible name, and a
              `<label for>` pointing at an id the control never receives is a
              dangling association — worse than no visible label at all. */}
          <div className="w7-field">
            <span className="w7-field-label">Member</span>
            <SearchInput
              value={filters.q}
              onChange={(next) => patchFilter({ q: next || null })}
              label="Search member"
              placeholder="Username or name"
            />
          </div>

          <Field label="Selection">
            {({ id }) => (
              <Select
                id={id}
                value={filters.selection}
                onChange={(event) => patchFilter({ sel: event.target.value || null })}
                options={selectionOptions}
              />
            )}
          </Field>

          <Field label="Side">
            {({ id }) => (
              <Select
                id={id}
                value={filters.side}
                onChange={(event) =>
                  patchFilter({ side: event.target.value === 'all' ? null : event.target.value })
                }
                options={BET_SIDE_FILTERS.map((entry) => ({ value: entry.id, label: entry.label }))}
              />
            )}
          </Field>

          <Field
            label="Status"
            hint={
              admitsSettled(filters.status)
                ? 'Settled, void and cancelled bets are listed for the record. They are not in the book above.'
                : undefined
            }
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={filters.status}
                onChange={(event) =>
                  patchFilter({ status: event.target.value === 'open' ? null : event.target.value })
                }
                options={BET_STATUS_FILTERS.map((entry) => ({
                  value: entry.id,
                  label: entry.label,
                }))}
              />
            )}
          </Field>

          <Field label="From" hint="Leave empty for every bet on this market.">
            {({ id, describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                value={filters.from}
                onChange={(event) => patchFilter({ from: event.target.value || null })}
              />
            )}
          </Field>

          <Field label="To" hint="Leave empty for every bet on this market.">
            {({ id, describedBy }) => (
              <Input
                id={id}
                type="date"
                aria-describedby={describedBy}
                value={filters.to}
                onChange={(event) => patchFilter({ to: event.target.value || null })}
              />
            )}
          </Field>

          <Field label="Rows per page">
            {({ id }) => (
              <Select
                id={id}
                value={String(filters.rows)}
                onChange={(event) =>
                  patchFilter({ rows: event.target.value === '100' ? null : event.target.value })
                }
                options={BET_ROW_CAPS.map((cap) => ({ value: String(cap), label: String(cap) }))}
              />
            )}
          </Field>

          <div className="w7-mb-actions">
            <Button
              variant="ghost"
              disabled={!filtered && filters.page === 1}
              onClick={() => onPatch(CLEARED_BET_FILTERS)}
            >
              Clear filters
            </Button>
          </div>
        </div>
        <p className="w7-mb-betnote">
          Event and market are the same on every row here, so they are not columns — they are in the
          CSV, where a file has to identify itself. Per-bet IP is not shown: it is a surveillance
          affordance and sits outside this console&rsquo;s boundary.
        </p>
      </div>

      {isError ? (
        <div style={{ padding: 'var(--w7-s4)' }}>
          <StateBlock
            icon="error"
            tone="error"
            title="The bet list could not be loaded"
            actions={
              <Button variant="secondary" icon="refresh" onClick={() => refetch()}>
                Try again
              </Button>
            }
          >
            {(error as Error)?.message}
          </StateBlock>
        </div>
      ) : (
        <>
          <DataTable
            label="Bets on this market"
            rows={rows}
            columns={columns}
            rowKey={(bet) => bet.id}
            density="compact"
            loading={isLoading}
            skeletonRows={10}
            showTotals={!isLoading && rows.length > 0}
            totalsLabel="Listed bets"
            initialSort={{ columnId: 'placed', direction: 'desc' }}
            empty={
              filtered ? (
                <StateBlock
                  icon="search"
                  title="No bets match these filters"
                  actions={
                    <Button variant="secondary" onClick={() => onPatch(CLEARED_BET_FILTERS)}>
                      Clear filters
                    </Button>
                  }
                >
                  Every bet on this market is still here — widen the status, the dates or the
                  selection to find it.
                </StateBlock>
              ) : (
                <StateBlock icon="exposure" title="No bets on this market">
                  Bets appear here the moment a member beneath you places one and it reaches the
                  exchange.
                </StateBlock>
              )
            }
          />
          <div className="w7-mb-betfoot">
            <Pager
              page={filters.page}
              pageCount={pageCount}
              onChange={(page) => onPatch({ page: page === 1 ? null : String(page) })}
              total={total}
              pageSize={filters.rows}
            />
          </div>
        </>
      )}
    </Card>
  )
}
